import { action, observable, reaction } from "mobx";
import { getReports, GetReportsResponse, LetterTemplateSummaryModel } from "@/api/Reports/getReports";
import { ReportTypeModel, CollectionIssueTypeCategoryModel } from "@/api/Reports";
import { WasteStreamModel, SuburbAndStreetsModel } from "@/api/Shared";
import { LoadingStore } from "./loadingStore";
import { postReportSearch, PostReportSearchRequest, PostReportSearchResponse } from "@/api/Reports/postReportSearch";
import { postReportExport, PostReportExportRequest } from "@/api/Reports/postReportExport";
import { NumberOfCollectionEventsModel, HeaderModel, generateLetters } from "@/api/Reports";
import { CollectionIssueEventModel } from "@/api/CollectionIssueHistory";
import { LetterTemplateModel } from "@/api/LetterTemplates";
import format from "date-fns/format";
import { DATE_PICKER_FORMAT } from "@/Constants";
import userStore from "@/store/userStore";
import notificationStore from "./notificationStore";

export class ReportFilters {
  @observable dateRange: string[] = [];
  @observable reportType: ReportTypeModel | null = null;
  @observable wasteStreams: WasteStreamModel[] = [];
  @observable suburbs: SuburbAndStreetsModel[] = [];
  @observable streets: string[] = [];
  @observable collectionIssueTypeCategories: CollectionIssueTypeCategoryModel[] = [];
  @observable letterTemplates: LetterTemplateSummaryModel[] = [];
}

export class ReportsStore {
  @observable reportTypes: ReportTypeModel[] = [];
  @observable wasteStreams: WasteStreamModel[] = [];
  @observable suburbs: SuburbAndStreetsModel[] = [];
  @observable collectionIssueTypeCategories: CollectionIssueTypeCategoryModel[] = [];
  @observable letterTemplates: LetterTemplateSummaryModel[] = [];
  @observable headers: HeaderModel[] = [];
  @observable results: CollectionIssueEventModel[] | NumberOfCollectionEventsModel[] = [];
  @observable selectedResults: CollectionIssueEventModel[] = [];
  @observable selectedLetterTemplate: LetterTemplateModel | null = null;
  @observable filters = new ReportFilters();
  @observable loading = new LoadingStore();
  @observable currentPage = 1;

  constructor() {
    reaction(
      () => ({
        reportType: this.filters.reportType,
        dateRange: this.filters.dateRange,
        wasteStreams: this.filters.wasteStreams,
        suburbs: this.filters.suburbs,
        streets: this.filters.streets,
        letterTemplates: this.filters.letterTemplates,
        collectionIssueTypes: this.filters.collectionIssueTypeCategories
      }),
      () => {
        this.resetResults();
        this.selectedResults = [];
      }
    );

    reaction(
      () => ({
        reportType: this.filters.reportType
      }),
      this.setInitialSelections
    );
  }

  @action.bound setInitialSelections() {
    if (!this.filters.reportType) this.filters.reportType = this.reportTypes[0];
    this.filters.dateRange = [format(new Date(), DATE_PICKER_FORMAT), format(new Date(), DATE_PICKER_FORMAT)];
    this.filters.suburbs = [];
    this.filters.streets = [];
    this.filters.wasteStreams = [];
    this.filters.letterTemplates = [];
    this.filters.collectionIssueTypeCategories = [];
  }

  @action.bound async fetchReports() {
    return this.loading.track(async () => {
      try {
        const response = await getReports();
        this.loadReport(response);
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading the report data");
      }
    }, "fetchReports");
  }

  @action.bound loadReport(response: GetReportsResponse) {
    this.reportTypes = response.reportTypes;
    this.wasteStreams = response.wasteStreams;
    this.suburbs = response.suburbsAndStreetNames;
    this.collectionIssueTypeCategories = response.collectionIssueTypeCategories;
    this.letterTemplates = response.letterTemplates;

    this.setInitialSelections();
  }

  @action.bound async fetchReportSearch() {
    if (!this.filters.reportType) return;

    const request: PostReportSearchRequest = {
      reportType: this.filters.reportType.reportTypeId,
      startAt: new Date(this.filters.dateRange[0]),
      endAt: new Date(this.filters.dateRange.length === 1 ? this.filters.dateRange[0] : this.filters.dateRange[1]),
      wasteStreamIds: this.filters.wasteStreams.length !== this.wasteStreams.length ? this.filters.wasteStreams.map(ct => ct.wasteStreamId) : [],
      suburbs: this.filters.suburbs.map(x => x.suburbName),
      streets: this.filters.streets,
      collectionIssueTypeCategories:
        this.filters.collectionIssueTypeCategories.length !== this.collectionIssueTypeCategories.length
          ? this.filters.collectionIssueTypeCategories.map(ct => ct.collectionIssueTypeCategoryId)
          : [],
      letterTemplateNames: this.filters.letterTemplates.map(lt => lt.letterTemplateName)
    };

    return this.loading.track(async () => {
      try {
        this.headers = [];
        this.results = [];
        this.selectedResults = [];
        this.currentPage = 1;
        const response = await postReportSearch(request);
        this.loadReportSearch(response);
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading the report");
      }
    }, "fetchReportSearch");
  }

  @action.bound loadReportSearch(response: PostReportSearchResponse) {
    this.headers = response.headers;
    this.results = response.results;
  }

  @action.bound resetResults() {
    this.headers = [];
    this.results = [];
  }

  @action.bound async generateLetters() {
    return this.loading.track(async () => {
      try {
        if (!this.selectedLetterTemplate) return;

        await generateLetters({ collectionIssueIds: this.selectedResults.map(r => r.id), letterTemplateId: this.selectedLetterTemplate.id });
      } catch (error) {
        notificationStore.error("An unexpected error occurred while generating the letters");
      }
    }, "generateLetters");
  }

  @action.bound async exportToCsv() {
    return this.loading.track(async () => {
      try {
        if (!userStore.activeCouncil) return;
        if (!this.filters.reportType) return;
        const request: PostReportExportRequest = {
          reportType: this.filters.reportType.reportTypeId,
          startAt: new Date(this.filters.dateRange[0]),
          endAt: new Date(this.filters.dateRange.length === 1 ? this.filters.dateRange[0] : this.filters.dateRange[1]),
          wasteStreamIds: this.filters.wasteStreams.length !== this.wasteStreams.length ? this.filters.wasteStreams.map(ct => ct.wasteStreamId) : [],
          suburbs: this.filters.suburbs.map(x => x.suburbName),
          streets: this.filters.streets,
          collectionIssueTypeCategories:
            this.filters.collectionIssueTypeCategories.length !== this.collectionIssueTypeCategories.length
              ? this.filters.collectionIssueTypeCategories.map(ct => ct.collectionIssueTypeCategoryId)
              : [],
          collectionEventIds: this.selectedResults.map(s => s.id),
          letterTemplateNames: this.filters.letterTemplates.map(lt => lt.letterTemplateName)
        };
        await postReportExport(request);
      } catch (error) {
        notificationStore.error("An unexpected error occurred while exporting the results");
      }
    }, "exportToCsv");
  }
}

const store = new ReportsStore();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).reportsStore = store;

export default store;
